import { ErrorHandler, Injectable, Injector, ɵRuntimeError, Inject, Optional } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppError } from './services/app-error';
import { ErrorRendererService } from './services/error-renderer.service';
import { ErrorReporterService } from './services/error-reporter.service';
import { IJsonApiErrorDocument } from './services/jsonapi-error.interface';
import { BaseException } from './base-exception';
import { ErrorPipelineFactory } from './pipeline/error-pipeline.factory';
import { ErrorPipeline } from './pipeline/error-pipeline';
import { ErrorPacket } from './pipeline/types';
import { BREADCRUMB_TRACKER_PORT } from './reporting/breadcrumbs/breadcrumb-tracker.token';
import { IBreadcrumbTrackerService } from '@app/common/observability/domain/services/breadcrumb-tracker.service';
import { getErrorReporterMetadata } from '@app/common/observability/helpers/error-reporter-metadata.helper';

export interface IHttpJsonApiError {
    error: IJsonApiErrorDocument;
    status?: number;
}

@Injectable()
export class CustomErrorHandlerService extends ErrorHandler {
    private readonly pipeline: ErrorPipeline<ErrorPacket, ErrorPacket>;

    public constructor(
        private injector: Injector,
        pipelineFactory: ErrorPipelineFactory,
        @Optional()
        @Inject(BREADCRUMB_TRACKER_PORT)
        private breadcrumbTrackerService?: IBreadcrumbTrackerService,
    ) {
        super();
        this.pipeline = pipelineFactory.create();
    }

    public handleError(
        error: IJsonApiErrorDocument | HttpErrorResponse | BaseException | ɵRuntimeError,
    ): void {
        // 1. Crear el paquete inicial
        const initialPacket: ErrorPacket = {
            data: error,
            result: undefined,
            extra: {
                timestamp: Date.now(),
                source: 'CustomErrorHandler',
            },
        };

        // 2. Ejecutar el pipeline
        const resultPacket: ErrorPacket = this.pipeline.run(initialPacket);

        // 3. El terminal garantiza que siempre hay result
        const app_error: AppError = resultPacket.result!;

        if (app_error.render) {
            const error_render: ErrorRendererService = this.injector.get(ErrorRendererService);
            error_render.render(app_error);
        }

        if (app_error.report) {
            if (this.isErrorWithResponsePayload(error)) {
                this.breadcrumbTrackerService?.jsExceptionWithResponse({
                    hasResponse: true,
                    ...getErrorReporterMetadata(error),
                });
            } else {
                this.breadcrumbTrackerService?.unhandledJsExceptionCaptured({
                    errorType: typeof error,
                });
            }

            const error_reporter: ErrorReporterService = this.injector.get(ErrorReporterService);
            error_reporter.report(app_error);
        }
    }

    private isErrorWithResponsePayload(error: unknown): boolean {
        if (!(error instanceof HttpErrorResponse)) {
            return false;
        }

        return Boolean(error.error);
    }
}
